import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { ACTION_POPUP_CONTENT_WIDTH_CLASS } from '../../../components/popup/popupLayoutSpec';
import { useInlineRowActions, ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useInlineRowActions';
import { useConfigurationListPagination } from '../../../Hooks/useConfigurationListPagination';
import { urlNameSegment } from '../../../utils/slugify';
import {
  AdminHeaderAction,
  AdminRowMenuItem,
  AdminPageTitle,
  AdminPanelTitle,
  AdminRowActionsButton,
  AdminFixedRowActionsMenu,
  AdminRowMenuIcon,
  AdminTableBody,
  AdminTableHeader,
  AdminTableMeta,
  ADMIN_PAGE_TITLE_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import {
  buildSampleValidationRules,
  formatBaseCheckSettings,
  formatConditionSettings,
  formatConditionsPreview,
  formatValidationDateTime,
  getBaseCheckLabel,
  getConditionTypeLabel,
  getTriggerLabel,
  getValidationModuleLabel,
  sortValidationRules,
} from '../../../data/validationConfig';

const tableCols =
  'minmax(0, 0.45fr) minmax(0, 1.2fr) minmax(0, 0.55fr) minmax(0, 0.9fr) minmax(0, 0.48fr) minmax(0, 0.75fr) minmax(0, 0.72fr) minmax(0, 0.95fr) minmax(0, 0.9fr) minmax(0, 0.7fr) minmax(0, 0.7fr) minmax(62px, 0.04fr)';

const formatViewAllCondition = (condition) =>
  [
    `Name: ${condition.name || getConditionTypeLabel(condition.type) || '—'}`,
    `Type: ${getConditionTypeLabel(condition.type) || '—'}`,
    `Settings: ${formatConditionSettings(condition)}`,
    `Validation Message: ${condition.message || '—'}`,
  ].join('\n');

const ViewAllModal = ({ title, items, onClose }) => (
  <div
    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4"
    onClick={onClose}
    role="presentation"
  >
    <div
      className={`bg-white rounded-xl shadow-lg ${ACTION_POPUP_CONTENT_WIDTH_CLASS} max-h-[90vh] overflow-y-auto`}
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="val-view-all-title"
    >
      <div className="relative px-5 pt-5 pb-3 border-b border-lightgray">
        <div id="val-view-all-title">
          <AdminPanelTitle className="text-offblack text-center">{title}</AdminPanelTitle>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 text-offblack hover:text-primaryblue"
          aria-label="Close"
        >
          <i className="fi fi-rs-cross text-sm" aria-hidden />
        </button>
      </div>
      <div className="px-5 py-4">
        <ul className="divide-y divide-lightgray border-y border-lightgray">
          {items.map((item, index) => (
            <li key={`${item}-${index}`} className="py-2.5 break-words">
              <AdminTableBody className="whitespace-pre-wrap">{item}</AdminTableBody>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

const Validation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [rules, setRules] = useState(() => sortValidationRules(buildSampleValidationRules()));
  const [viewAllConditions, setViewAllConditions] = useState(null);

  const { rowActionsOpen, rowActionsRefs, tableScrollRef, rowActionsMenuLayout, rowActionsMenuRef, rowActionsMenuPlacementReady, toggleRowActions, closeRowActions } = useInlineRowActions(ROW_ACTIONS_MENU_HEIGHT.DOUBLE);

  const {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedItems: paginatedRules,
    rangeStart,
    rangeEnd,
    totalCount,
    showPagination,
  } = useConfigurationListPagination(rules);

  useEffect(() => {
    const incoming = location.state?.rules;
    if (Array.isArray(incoming)) {
      setRules(sortValidationRules(incoming));
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const openRuleView = (rule) => {
    navigate(`/admin/configuration/validation/${urlNameSegment(rule.ruleName)}`, {
      state: { rule, viewMode: true, existingRules: rules },
    });
  };

  const columnHeaders = [
    'Rule ID',
    'Rule Name',
    'Module',
    'Entry Point',
    'Trigger',
    'Input Field',
    'Base Check',
    'Conditions',
    'Condition Types',
    'Last Updated',
    'Last Updated By',
    'Actions',
  ];

  return (
    <AdminLayout>
      <div className="min-w-0">
        <div className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
          <AdminPageTitle className="!mb-0">Validation</AdminPageTitle>
          <AdminHeaderAction onClick={() => navigate('/admin/configuration/validation/add', { state: { existingRules: rules } })}>
            Add Rule
          </AdminHeaderAction>
        </div>

        <DashboardMainContainer className={`${ADMIN_TABLE_CLASS} max-w-full min-w-0`}>
          <div ref={tableScrollRef} className="overflow-x-auto">
            <div
              className="grid w-full py-3 text-offblack border-b border-lightgray px-4 gap-x-3 gap-y-2"
              style={{ gridTemplateColumns: tableCols }}
            >
              {columnHeaders.map((label) => (
                <div
                  key={label}
                  className={`flex items-center min-w-0 ${label === 'Actions' ? 'justify-center shrink-0' : ''}`}
                >
                  <AdminTableHeader className={label === 'Actions' ? '' : 'truncate'}>{label}</AdminTableHeader>
                </div>
              ))}
            </div>

            {paginatedRules.map((rule) => {
              const conditionsPreview = formatConditionsPreview(rule.conditions);
              return (
                <div
                  key={rule.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openRuleView(rule)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openRuleView(rule);
                    }
                  }}
                  className="grid w-full py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-3 gap-y-2 cursor-pointer rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-primaryblue focus-visible:ring-offset-1"
                  style={{ gridTemplateColumns: tableCols }}
                >
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableMeta className="truncate block" title={rule.id}>
                      {rule.id}
                    </AdminTableMeta>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block" title={rule.ruleName}>
                      {rule.ruleName}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block">{getValidationModuleLabel(rule.module)}</AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block">{rule.entryPoint}</AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block">{getTriggerLabel(rule.trigger)}</AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block">{rule.inputField || '—'}</AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block" title={formatBaseCheckSettings(rule)}>
                      {getBaseCheckLabel(rule.baseCheck)}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    {conditionsPreview.extraCount > 0 ? (
                      <AdminTableBody className="block min-w-0 w-full" title={conditionsPreview.lines.join(', ')}>
                        <span className="flex items-center min-w-0 w-full gap-1">
                          <span className="min-w-0 truncate">{conditionsPreview.lines[0]}</span>
                          <button
                            type="button"
                            className="text-primaryblue hover:underline shrink-0 whitespace-nowrap"
                            onClick={(e) => {
                              e.stopPropagation();
                              setViewAllConditions((rule.conditions || []).map(formatViewAllCondition));
                            }}
                          >
                            +{conditionsPreview.extraCount} more
                          </button>
                        </span>
                      </AdminTableBody>
                    ) : (
                      <AdminTableBody className="truncate block" title={conditionsPreview.text}>
                        {conditionsPreview.text}
                      </AdminTableBody>
                    )}
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block" title={conditionsPreview.typesText}>
                      {conditionsPreview.typesText}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block" title={formatValidationDateTime(rule.lastUpdated)}>
                      {formatValidationDateTime(rule.lastUpdated)}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block">{rule.lastUpdatedBy || '—'}</AdminTableBody>
                  </div>
                  <div className="flex items-center justify-center shrink-0 overflow-visible" onClick={(e) => e.stopPropagation()}>
                    <div
                      className="relative inline-flex"
                      ref={(el) => {
                        rowActionsRefs.current[rule.id] = el;
                      }}
                    >
                      <AdminRowActionsButton
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRowActions(rule.id);
                        }}
                        aria-expanded={!!rowActionsOpen[rule.id]}
                        title="Actions"
                        aria-label="Row actions"
                      >
                        <AdminRowMenuIcon />
                      </AdminRowActionsButton>
                      {rowActionsOpen[rule.id] && rowActionsMenuLayout && (
                        <AdminFixedRowActionsMenu layout={rowActionsMenuLayout} menuRef={rowActionsMenuRef} placementReady={rowActionsMenuPlacementReady}>
                          <AdminRowMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              closeRowActions();
                              openRuleView(rule);
                            }}
                          >
                            View
                          </AdminRowMenuItem>
                          <AdminRowMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              closeRowActions();
                              navigate(`/admin/configuration/validation/${urlNameSegment(rule.ruleName)}/edit`, {
                                state: { rule, existingRules: rules },
                              });
                            }}
                          >
                            Edit
                          </AdminRowMenuItem>
                        </AdminFixedRowActionsMenu>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalCount}
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>

      {viewAllConditions ? (
        <ViewAllModal
          title="View All Conditions"
          items={viewAllConditions}
          onClose={() => setViewAllConditions(null)}
        />
      ) : null}
    </AdminLayout>
  );
};

export default Validation;
